import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import DefaultLayout from '~/layout/components/DefaultLayout';
import { trackLoginPageView, trackAccountPageView } from '~/login/actions';
import LoginSlidingPanels, {
  PanelStates,
} from '~/login/components/LoginSlidingPanels/LoginSlidingPanels';
import { isAuthTypeAuthenticated } from '~/shared/utils/isFullyAuthenticated';
import {
  desktop,
  Heading,
  mobile,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import { AuthType, useSession } from '~/techstyle-shared/redux-core';

import AccountPageMenu from '../AccountPageMenu';

const PasswordChallengeWrapper = styled.main`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  text-align: center;
`;

const Wrapper = styled.main`
  ${desktop`
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.colors.backgroundAccounts};
  `}

  background: ${({ theme }) => theme.colors.backgroundAccountsLight};
  min-height: 100vh;
`;

const ContentWrapper = styled.div`
  display: flex;
  order: 2;
  flex: 1;
  grid-area: 2;
  flex-direction: column;

  ${desktop`
    justify-content: center;
  `}

  ${mobile`
    /* what is the width for desktop if this is set to 100%? */
    width: 100%;
    padding: ${(props) => props.theme.spacing.small}px 0;
  `};
`;

const Body = styled.div`
  width: 100%;
  margin: 0 auto;

  ${desktop`
    flex: 1;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    align-content: flex-start;
    max-width: 868px;
    display: grid;
    gap: 12px;
    grid-template-columns: auto 1fr;
    padding-top: 58px;
    padding-bottom: 40px;
  `};
`;

const StyledAccountPageMenu = styled(AccountPageMenu)`
  grid-area: 2 / 1 / 3 / 2;
  position: relative;
  z-index: 3;
`;

const Title = styled(Heading).attrs({
  level: 1,
  align: 'center',
})`
  width: 100%;
  margin-bottom: 24px;
  font-size: ${({ theme }) => theme.account.pageHeadingSize};
  font-weight: 700;
  letter-spacing: ${0.5 / 24}em;
  line-height: 26.36px;
  grid-area: 2 / 2 / 1;
  order: 0;
  position: relative;
  text-transform: uppercase;
  ${mobile`
    display: none;
  `};
  ${({ theme }) => theme.account.pageTitle}
`;

export default function AccountPageTemplate({
  title,
  children,
  titleContainer,
  mobileTitleContainer,
  mobileTitleWithDropDownMenu,
  ...props
}) {
  const { authType } = useSession();
  const { isMobile } = useBreakpoint();
  const intl = useIntl();
  const dispatch = useDispatch();

  const passwordChallengeRequired = !isAuthTypeAuthenticated(authType);

  if (typeof title === 'object' && title.id) {
    title = intl.formatMessage(title);
  }

  // When accessing a PII-gated part of the site, if the user does not have the correct authType (2 === autologin, 1 === anonymous),
  // prevent firing the event unless the user is authenticated. After successful completion of the PII auth gate,
  // the actual PageView event can be accurately fired.
  useEffect(() => {
    if (passwordChallengeRequired) {
      const pageName = 'piigate/login';
      dispatch(
        trackLoginPageView({
          pageName,
          name: pageName,
        })
      );
    } else {
      dispatch(trackAccountPageView());
    }
  }, [dispatch, passwordChallengeRequired]);

  return (
    <DefaultLayout
      title={title}
      noHeader={Boolean(isMobile && mobileTitleContainer)}
    >
      {passwordChallengeRequired ? (
        <PasswordChallengeWrapper>
          <LoginSlidingPanels
            defaultPanel={
              authType === AuthType.AUTOLOGIN
                ? PanelStates.AUTHENTICATE_USER
                : PanelStates.USERNAME_INPUT
            }
            isPiiAuthGate
          />
        </PasswordChallengeWrapper>
      ) : (
        <Wrapper {...props}>
          <Body>
            {isMobile && mobileTitleContainer}
            {isMobile && mobileTitleWithDropDownMenu && (
              <>
                <StyledAccountPageMenu title={title} />
                {mobileTitleWithDropDownMenu}
              </>
            )}
            {!mobileTitleContainer && !mobileTitleWithDropDownMenu && (
              <StyledAccountPageMenu title={title} />
            )}

            {titleContainer || (title && <Title>{title}</Title>)}
            <ContentWrapper>{children}</ContentWrapper>
          </Body>
        </Wrapper>
      )}
    </DefaultLayout>
  );
}
AccountPageTemplate.propTypes = {
  children: PropTypes.node,
  mobileTitleContainer: PropTypes.node,
  mobileTitleWithDropDownMenu: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  titleContainer: PropTypes.node,
};

AccountPageTemplate.ContentWrapper = ContentWrapper;
AccountPageTemplate.Title = Title;
AccountPageTemplate.Menu = StyledAccountPageMenu;
